import {
  useFetchJob,
  useFetchTask,
  useNextPendingTask,
  useCompleteJobTask,
  useCreateReviewTasks,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { JobType } from "@augmodo/lebron-browser-client";
import { ulid, ulidToUUID } from "ulid";
import { BoxDrawImage } from "@labeling-tools/ui-components/src/box-draw-image";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useProfileHome } from "@/hooks/useProfileHome";
import { ViewMode } from "@labeling-tools/out-of-stock/src/types";
import { useFetchParcel } from "@labeling-tools/api/lebron/react-query-hooks";
import { getNextPendingTaskData } from "@labeling-tools/out-of-stock/src/utils/helpers";
import { useImageGallery } from "@labeling-tools/api/lebron/react-query-hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { LoadingOverlay } from "@mantine/core";
import {
  BOX_DRAW_SIDEBAR_WIDTH,
  PLAY_MODE_JOB_TYPE,
} from "@labeling-tools/out-of-stock/src/config/data";
import {
  useAppDispatch,
  useAppSelector,
} from "@labeling-tools/out-of-stock/src/store/hooks";
import { setIsPlayMode } from "@labeling-tools/out-of-stock/src/store";
import { BoxDrawSidebar } from "@labeling-tools/out-of-stock/src/components/box-draw/BoxDrawSidebar";
import { TaskCounter } from "@labeling-tools/out-of-stock/src/components/TaskCounter";
import { useBoxDrawState } from "@labeling-tools/out-of-stock/src/hooks/useBoxDrawState";
import { useFetchNextPlayModeJob } from "@/hooks/useFetchNextPlayModeJob";

export const BoxDrawTask = ({ mode }: { mode?: ViewMode }) => {
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const { jobId } = useParams<{ jobId: string }>();

  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const dispatch = useAppDispatch();
  const isPlayMode = useAppSelector((state) => state.oos.isPlayMode);
  const [noNextJobAvailable, setNoNextJobAvailable] = useState(false);

  const { data: job } = useFetchJob(jobId ?? "");

  const {
    data: nextPendingTask,
    isLoading: nextPendingTaskLoading,
    isFetching: nextPendingTaskFetching,
    isError: nextPendingTaskError,
  } = mode === "admin"
    ? useFetchTask(jobId, taskId)
    : useNextPendingTask(jobId);

  // If no more pending tasks (404), auto-advance in play mode or navigate home
  useEffect(() => {
    if (!nextPendingTaskError) return;
    if (!isPlayMode || noNextJobAvailable) {
      navigate(`${jobsPath}?tab=box-draw`);
    }
  }, [
    nextPendingTaskError,
    isPlayMode,
    noNextJobAvailable,
    navigate,
    jobsPath,
  ]);

  const onNoNextJobFound = useCallback(() => {
    setNoNextJobAvailable(true);
    dispatch(setIsPlayMode(false));
    navigate(`${jobsPath}?tab=box-draw`);
  }, [dispatch, navigate, jobsPath]);

  useFetchNextPlayModeJob({
    enabled: !!nextPendingTaskError && isPlayMode && !noNextJobAvailable,
    jobId,
    jobType: PLAY_MODE_JOB_TYPE["box-draw"].jobType,
    route: PLAY_MODE_JOB_TYPE["box-draw"].route,
    // Box-draw tasks span a whole shelf image, potentially multiple
    // aisles/planograms -- there's no single "the" planogram to prefer.
    livePlanogramId: undefined,
    onNoJobFound: onNoNextJobFound,
  });

  // Guard: only allow OUT_OF_STOCK_BOX_DRAW_TASKS jobs on this page
  useEffect(() => {
    if (job && job.type !== JobType.OUT_OF_STOCK_BOX_DRAW_TASKS) {
      navigate(jobsPath);
    }
  }, [job, navigate, jobsPath]);

  // get parcel data
  const event = nextPendingTask?.data;
  const { data: parcel, isLoading: parcelLoading } = useFetchParcel(
    event?.parcel_id as string | undefined,
  );
  const nextPendingTaskData = getNextPendingTaskData(
    parcel,
    event,
    nextPendingTask,
  );

  // fetch image gallery - main image
  const {
    mutate: mainImageMutation,
    data: mainImageGallery,
    isPending: mainImageLoading,
  } = useImageGallery();

  // missing item image url
  const missingItemImageUrl = mainImageGallery?.images[0]?.image;

  // fetch main image
  useEffect(() => {
    if (nextPendingTaskData?.image_id) {
      mainImageMutation({
        image_ids: [nextPendingTaskData.image_id],
      });
    }
  }, [nextPendingTaskData?.id, mainImageMutation]);

  // Format empty areas for ImageWithBoundingBox
  const emptyAreasPoints = useMemo(() => {
    if (!nextPendingTaskData?.data?.empty_areas) return [];
    return nextPendingTaskData.data.empty_areas.map((area: any) => ({
      points: area.points || [],
    }));
  }, [nextPendingTaskData?.data?.empty_areas]);

  // Aisle detections for spatial lookup (index matches aisle_to_planogram)
  const aisleDetections = useMemo(
    () => (parcel?.query as any)?.aisle_detections ?? [],
    [parcel?.query],
  );

  // Find the aisle detection that fully contains all of the given points.
  // Returns the aisle index or null if any point falls outside every aisle.
  const findAisleForPoints = useCallback(
    (points: number[][]): number | null => {
      if (!points?.length || aisleDetections.length === 0) return null;

      for (let i = 0; i < aisleDetections.length; i++) {
        const ap = aisleDetections[i]?.points;
        if (!ap || ap.length < 4) continue;
        const xs = ap.map((p: number[]) => p[0]);
        const ys = ap.map((p: number[]) => p[1]);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const allInside = points.every(
          (p) => p[0] >= minX && p[0] <= maxX && p[1] >= minY && p[1] <= maxY,
        );
        if (allInside) return i;
      }
      return null;
    },
    [aisleDetections],
  );

  // Use custom hook for box draw state management
  const {
    selectedExistingBoxIndex,
    setSelectedExistingBoxIndex,
    selectedUserDrawnBoxIndex,
    setSelectedUserDrawnBoxIndex,
    hiddenExistingBoxIndices,
    hiddenUserDrawnBoxIndices,
    deletedBoxIndices,
    userDrawnBoxes,
    handleBoxView,
    handleBoxDelete,
    handleBoxRestore,
    handleUserDrawnBoxesChange: rawHandleUserDrawnBoxesChange,
    resetBoxDrawState,
  } = useBoxDrawState();

  // Wrap the boxes change handler to tag each box with its aisle_index
  const handleUserDrawnBoxesChange = useCallback(
    (boxes: typeof userDrawnBoxes) => {
      const tagged = boxes.map((box) => {
        if (box.aisle_index !== undefined) return box;
        const aisleIndex = findAisleForPoints(box.points);
        return { ...box, aisle_index: aisleIndex };
      });
      rawHandleUserDrawnBoxesChange(tagged);
    },
    [findAisleForPoints, rawHandleUserDrawnBoxesChange, aisleDetections],
  );

  const queryClient = useQueryClient();
  const { mutateAsync: completeJobTask, isPending: isSubmitting } =
    useCompleteJobTask(jobId ?? "", nextPendingTask?.id ?? "");
  const { mutateAsync: createReviewTasks } = useCreateReviewTasks();

  const handleSubmit = async () => {
    if (!jobId || !nextPendingTask?.id || !parcel) return;

    // Build the confirmed empty areas list (existing non-deleted + user-drawn "draw" boxes)
    // Objects follow empty_area_detections shape from OoSParcelSchema
    const originalAreas = nextPendingTaskData?.data?.empty_areas ?? [];
    const confirmedExistingAreas = originalAreas.filter(
      (_: any, i: number) => !deletedBoxIndices.has(i),
    );
    const drawnEmptyAreas = userDrawnBoxes
      .filter((box) => box.type === "draw")
      .map((box) => ({
        id: ulidToUUID(ulid(Date.now())).toLocaleLowerCase(), // format new ulids as uuidv4, use lowercase for consistency
        points: box.points,
        confidence: 1.0,
        class_index: 0,
      }));
    const allEmptyAreas = [...confirmedExistingAreas, ...drawnEmptyAreas];
    const emptyAreasSource: ("auto" | "human")[] = allEmptyAreas.map(
      (_, index) => (index < confirmedExistingAreas.length ? "auto" : "human"),
    );

    const unblurredPersons = userDrawnBoxes
      .filter((box) => box.type === "draw-user")
      .map((box) => ({
        points: box.points,
        confidence: 1.0,
        class_index: 0,
      }));

    // Map allEmptyAreas index → original parcel detection index
    // (accounting for deleted boxes that were filtered out)
    const confirmedOriginalIndices = Array.from(
      { length: originalAreas.length },
      (_, i) => i,
    ).filter((i) => !deletedBoxIndices.has(i));

    const originalEmptyAreaToAisle = parcel.query.empty_area_to_aisle ?? [];

    // Build a reindexed empty_area_to_aisle aligned with allEmptyAreas:
    // - Confirmed original areas map back to their parcel aisle index
    // - User-drawn areas use the aisle_index assigned at draw time
    const drawnBoxesWithAisle = userDrawnBoxes.filter(
      (box) => box.type === "draw",
    );
    const reindexedEmptyAreaToAisle = allEmptyAreas.map(
      (_area: any, index: number) => {
        if (index < confirmedOriginalIndices.length) {
          const originalIndex = confirmedOriginalIndices[index];
          return originalEmptyAreaToAisle[originalIndex] ?? null;
        }
        const drawnIndex = index - confirmedOriginalIndices.length;
        return drawnBoxesWithAisle[drawnIndex]?.aisle_index ?? null;
      },
    );

    try {
      // 1. Complete the box draw task with the new result schema
      await completeJobTask({
        result: {
          deleted_empty_areas: Array.from(deletedBoxIndices),
          empty_areas: allEmptyAreas,
          empty_area_to_aisle: reindexedEmptyAreaToAisle,
          empty_areas_source: emptyAreasSource,
          empty_areas_no_parent: [],
          unblurred_persons: unblurredPersons,
          testing: job?.testing ?? false,
        },
      });

      // 2. Create an OUT_OF_STOCK_CONFIRMATION review task per confirmed empty area
      if (allEmptyAreas.length > 0) {
        const { query, references, store } = parcel;
        const imageId = query.image_id;

        const referenceImages = references.map((reference) => ({
          image_id: reference.image_id,
          product_detections: reference.product_detections ?? [],
          product_ids_top_n: reference.product_ids_top_n,
          product_dist_top_n: reference.product_dist_top_n,
          empty_area_to_product_mapping: reference.empty_area_to_product,
        }));

        const tasksWithAisle = allEmptyAreas
          .map((_, index) => ({
            index,
            aisleIdx: reindexedEmptyAreaToAisle[index],
          }))
          .filter(({ aisleIdx }) => aisleIdx != null);

        const tasks = tasksWithAisle.map(({ index, aisleIdx }) => {
          return {
            id: ulidToUUID(ulid(Date.now())).toLocaleLowerCase(),
            type: "OUT_OF_STOCK_CONFIRMATION" as any,
            data: {
              image_id: imageId,
              empty_areas: allEmptyAreas,
              query_empty_area_index: index,
              reference_images: referenceImages,
              internal_store_id: store.id,
              store: store,
              parcel_id: parcel.parcel_id,
              box_draw_task_id: nextPendingTask.id,
              deleted_empty_areas: Array.from(deletedBoxIndices),
              empty_areas_source: emptyAreasSource,
              product_to_aisle: query.product_to_aisle ?? [],
              product_ids: query.product_ids ?? [],
              aisle_index: aisleIdx,
              empty_area_to_aisle: reindexedEmptyAreaToAisle,
              empty_areas_no_parent: [],
              unblurred_persons: unblurredPersons,
            },
            image_id: imageId,
          };
        });

        console.log({ tasks });

        await createReviewTasks({
          store_id: store.id,
          task_type: "OUT_OF_STOCK_CONFIRMATION" as any,
          tasks,
          testing: job?.testing ? true : false, // if job has testing flag set, pass along the flag
        });
      }

      // 3. Reset box draw state before loading the next task
      resetBoxDrawState();

      queryClient.invalidateQueries({
        queryKey: ["nextPendingTask", jobId],
      });
    } catch (error) {
      console.error("Failed to submit box draw task:", error);
    }
  };

  // Dev tool: dump task/parcel data as JSON for coverage task sample data
  const handleDumpData = useCallback(() => {
    const dump = {
      job,
      task: nextPendingTask,
      parcel,
      imageUrl: missingItemImageUrl,
      taskData: nextPendingTaskData,
    };
    const blob = new Blob([JSON.stringify(dump, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `box-draw-dump-${jobId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    job,
    nextPendingTask,
    parcel,
    missingItemImageUrl,
    nextPendingTaskData,
    jobId,
  ]);

  if (
    mainImageLoading ||
    nextPendingTaskLoading ||
    nextPendingTaskFetching ||
    parcelLoading
  ) {
    return (
      <div className="flex justify-center items-center">
        <LoadingOverlay visible={true} />
      </div>
    );
  }

  return (
    <div
      className="pb-[75px] relative"
      style={{ paddingRight: BOX_DRAW_SIDEBAR_WIDTH }}
    >
      {mode !== "admin" && <TaskCounter taskId={nextPendingTask?.id} />}
      {import.meta.env.DEV && (
        <button
          onClick={handleDumpData}
          style={{
            position: "fixed",
            bottom: 8,
            left: 8,
            zIndex: 9999,
            padding: "4px 8px",
            fontSize: 11,
            background: "#333",
            color: "#0f0",
            border: "1px solid #555",
            borderRadius: 4,
            cursor: "pointer",
            opacity: 0.7,
          }}
        >
          Dump Data (Dev)
        </button>
      )}
      <div
        className={"relative flex justify-center items-center max-w-lg mx-auto"}
      >
        {missingItemImageUrl && (
          <BoxDrawImage
            src={missingItemImageUrl}
            alt="Box Draw Task"
            existingBoxes={emptyAreasPoints}
            hiddenExistingBoxIndices={hiddenExistingBoxIndices}
            selectedExistingBoxIndex={selectedExistingBoxIndex}
            onSelectedExistingBoxChange={setSelectedExistingBoxIndex}
            userDrawnBoxes={userDrawnBoxes}
            hiddenUserDrawnBoxIndices={hiddenUserDrawnBoxIndices}
            onUserDrawnBoxesChange={handleUserDrawnBoxesChange}
            selectedUserDrawnBoxIndex={selectedUserDrawnBoxIndex}
            onSelectedUserDrawnBoxChange={setSelectedUserDrawnBoxIndex}
            variant="empty"
          />
        )}
      </div>
      <BoxDrawSidebar
        existingBoxes={emptyAreasPoints}
        hiddenExistingBoxIndices={hiddenExistingBoxIndices}
        hiddenUserDrawnBoxIndices={hiddenUserDrawnBoxIndices}
        deletedBoxIndices={deletedBoxIndices}
        selectedExistingBoxIndex={selectedExistingBoxIndex}
        selectedUserDrawnBoxIndex={selectedUserDrawnBoxIndex}
        userDrawnBoxes={userDrawnBoxes}
        onBoxView={handleBoxView}
        onBoxDelete={handleBoxDelete}
        onBoxRestore={handleBoxRestore}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};
